import React, { useEffect, useState, useCallback } from "react";
import { api, fmt } from "@/lib/api";

const BAND_COLOR = {
  high: "#0a7d3a",
  medium: "#9b6b00",
  low: "#6b6b6b",
};

const BAND_LABEL = { high: "High", medium: "Medium", low: "Low" };

/**
 * GOV-025 Recognition Engine drawer.
 *
 * Split-by-Band acceptance UX:
 *   - HIGH (>= 0.90): single-click acceptance.
 *   - MEDIUM (0.75–0.89): confirmation dialog with signals/evidence.
 *   - LOW (< 0.75): override-only path with mandatory rationale.
 */
export function RecognitionPanel({ periodId, periodStatus, onChanged }) {
  const [data, setData] = useState({ items: [], kpis: {}, latest_run: null });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("open");
  const [confirmRec, setConfirmRec] = useState(null); // {rec, mode: "medium" | "low"}
  const [error, setError] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);

  const reload = useCallback(() => {
    setLoading(true);
    api.listRecommendations(periodId, filter !== "all" ? { status: filter } : {})
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [periodId, filter]);

  useEffect(() => { reload(); }, [reload]);

  async function runEngine() {
    setError(null); setBusy(true);
    try {
      await api.runRecognition(periodId);
      reload();
    } catch (e) {
      setError(e?.response?.data?.detail || "Recognition run failed.");
    } finally { setBusy(false); }
  }

  async function acceptHigh(rec) {
    setError(null); setBusy(true);
    try {
      await api.acceptRecommendation(rec.id, {});
      reload();
      onChanged && onChanged();
    } catch (e) {
      setError(e?.response?.data?.detail || "Acceptance failed.");
    } finally { setBusy(false); }
  }

  async function confirmMedium() {
    if (!confirmRec) return;
    setError(null); setBusy(true);
    try {
      await api.acceptRecommendation(confirmRec.rec.id, { confirmed_band: "medium" });
      setConfirmRec(null);
      reload();
      onChanged && onChanged();
    } catch (e) {
      setError(e?.response?.data?.detail || "Acceptance failed.");
    } finally { setBusy(false); }
  }

  async function overrideLow(rationale) {
    if (!confirmRec) return;
    if (!rationale.trim()) { setError("Rationale is required for override."); return; }
    setError(null); setBusy(true);
    try {
      await api.overrideRecommendation(confirmRec.rec.id, rationale.trim());
      setConfirmRec(null);
      reload();
      onChanged && onChanged();
    } catch (e) {
      setError(e?.response?.data?.detail || "Override failed.");
    } finally { setBusy(false); }
  }

  async function reject(rec) {
    const reason = window.prompt(`Reject this recommendation? Provide a reason:`);
    if (!reason || !reason.trim()) return;
    setError(null); setBusy(true);
    try {
      await api.rejectRecommendation(rec.id, reason.trim());
      reload();
    } catch (e) {
      setError(e?.response?.data?.detail || "Reject failed.");
    } finally { setBusy(false); }
  }

  // Bulk-accept (split-by-band preserved server-side).  Prompts once so
  // the Controller can't fat-finger a batch of MEDIUM acceptances by
  // accident.  Emits a toast-style summary from the server response.
  async function bulkAccept(bands) {
    const highCount = k.high || 0;
    const medCount = k.medium || 0;
    let toAccept, prompt;
    if (bands.length === 1 && bands[0] === "high") {
      toAccept = highCount;
      prompt = `Accept all ${highCount} HIGH-band recommendation${highCount === 1 ? "" : "s"} in one shot?

Each acceptance creates a governed MatchGroup and is audit-logged individually.
LOW-band rows are untouched.`;
    } else {
      toAccept = highCount + medCount;
      prompt = `Accept all ${highCount} HIGH and ${medCount} MEDIUM band recommendations (${toAccept} total) in one shot?

Each acceptance creates a governed MatchGroup and is audit-logged individually.
LOW-band rows still require an explicit override with rationale.`;
    }
    if (toAccept === 0) return;
    if (!window.confirm(prompt)) return;
    setError(null); setBusy(true); setBulkResult(null);
    try {
      const res = await api.bulkAcceptRecommendations(periodId, bands);
      reload();
      onChanged && onChanged();
      const summary = `Accepted ${res.accepted_count} of ${res.accepted_count + res.failed_count} · Bands: ${res.bands.join(" + ")}`;
      if (res.failed_count > 0) {
        setBulkResult({
          ok: false,
          text: `${summary}.  ${res.failed_count} could not be applied (typically because the same transaction was matched by an earlier row in the batch).  Re-run Recognition to refresh the remaining candidates.`,
          failures: res.failures,
        });
      } else {
        setBulkResult({ ok: true, text: `${summary}.  Variance should have moved in the right rail — re-check now.` });
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Bulk accept failed.");
    } finally { setBusy(false); }
  }

  const k = data.kpis || {};
  const locked = periodStatus === "locked";

  return (
    <div data-testid="recognition-panel">
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: 12, gap: 12,
      }}>
        <div>
          <div className="heading h3" style={{ marginBottom: 2 }}>Recognition Engine</div>
          <div className="muted" style={{ fontSize: 12 }}>
            GOV-025 · match.v1.1.0 · deterministic rule-based · Controller authority required
          </div>
        </div>
        <button
          className="btn primary btn-sm"
          onClick={runEngine}
          disabled={busy || locked}
          data-testid="rec-run-btn"
        >{busy ? "Running…" : "Run Recognition"}</button>
      </div>

      {data.latest_run && (
        <div className="muted" style={{ fontSize: 11, marginBottom: 10 }}>
          Last run · {fmt.dateTime(data.latest_run.created_at)} · scanned {data.latest_run.transactions_scanned}
          {" · "}emitted {data.latest_run.recommendations_emitted}
          {data.latest_run.recommendations_expired > 0 && <> · expired {data.latest_run.recommendations_expired}</>}
        </div>
      )}

      <div data-testid="rec-kpis" style={{
        display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 6, marginBottom: 14,
      }}>
        <Kpi label="High" value={k.high || 0} color={BAND_COLOR.high} testid="rec-kpi-high" />
        <Kpi label="Medium" value={k.medium || 0} color={BAND_COLOR.medium} testid="rec-kpi-medium" />
        <Kpi label="Low" value={k.low || 0} color={BAND_COLOR.low} testid="rec-kpi-low" />
        <Kpi label="Accepted" value={k.accepted || 0} testid="rec-kpi-accepted" />
        <Kpi label="Rejected" value={k.rejected || 0} testid="rec-kpi-rejected" />
      </div>

      {/* Bulk accept — Controller convenience over the split-by-band path. */}
      {((k.high || 0) + (k.medium || 0) > 0) && !locked && (
        <div data-testid="rec-bulk-actions" style={{
          display: "flex", gap: 8, marginBottom: 12, alignItems: "center",
          padding: "8px 10px", border: "1px dashed var(--line)", borderRadius: 6,
          background: "var(--surface-elev)",
        }}>
          <span className="muted" style={{ fontSize: 12, marginRight: "auto" }}>
            Bulk accept · governed path · one audit row per acceptance
          </span>
          <button
            className="btn btn-sm"
            disabled={busy || (k.high || 0) === 0}
            onClick={() => bulkAccept(["high"])}
            data-testid="rec-bulk-accept-high"
            title="Accept every HIGH-band recommendation in one shot"
          >
            Accept all HIGH ({k.high || 0})
          </button>
          <button
            className="btn primary btn-sm"
            disabled={busy || ((k.high || 0) + (k.medium || 0)) === 0}
            onClick={() => bulkAccept(["high", "medium"])}
            data-testid="rec-bulk-accept-high-medium"
            title="Accept every HIGH + MEDIUM band recommendation in one shot"
          >
            Accept all HIGH + MEDIUM ({(k.high || 0) + (k.medium || 0)})
          </button>
        </div>
      )}

      {bulkResult && (
        <div
          data-testid="rec-bulk-result"
          role="status"
          style={{
            padding: "8px 10px", marginBottom: 12, fontSize: 12,
            border: "1px solid " + (bulkResult.ok ? "#0a7d3a" : "#9b6b00"),
            background: bulkResult.ok ? "rgba(10, 125, 58, 0.06)" : "rgba(155, 107, 0, 0.06)",
            color: bulkResult.ok ? "#0a4e26" : "#5e4000",
            borderRadius: 6,
            display: "flex", gap: 8, alignItems: "flex-start", justifyContent: "space-between",
          }}
        >
          <span>{bulkResult.text}</span>
          <button
            onClick={() => setBulkResult(null)}
            data-testid="rec-bulk-result-dismiss"
            className="btn ghost btn-sm" style={{ padding: "0 6px", flexShrink: 0 }}
            title="Dismiss"
          >×</button>
        </div>
      )}

      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        {["open", "accepted", "rejected", "overridden", "expired", "all"].map(s => (
          <button
            key={s}
            className={"btn btn-sm" + (filter === s ? " primary" : "")}
            onClick={() => setFilter(s)}
            data-testid={`rec-filter-${s}`}
            style={{ textTransform: "capitalize", padding: "3px 10px", fontSize: 11 }}
          >{s}</button>
        ))}
      </div>

      {error && <div data-testid="rec-error" className="muted" style={{ color: "var(--blocker)", fontSize: 12, marginBottom: 8 }}>{error}</div>}

      {loading ? (
        <div className="muted">Loading…</div>
      ) : data.items.length === 0 ? (
        <div className="empty" data-testid="rec-empty">
          No {filter === "all" ? "" : filter} recommendations for this period.
          {filter === "open" && " Click \"Run Recognition\" to scan."}
        </div>
      ) : (
        data.items.map(rec => (
          <RecommendationRow
            key={rec.id}
            rec={rec}
            busy={busy}
            locked={locked}
            onAccept={(r) => {
              if (r.confidence_band_at_emit === "high") acceptHigh(r);
              else if (r.confidence_band_at_emit === "medium") setConfirmRec({ rec: r, mode: "medium" });
              else setConfirmRec({ rec: r, mode: "low" });
            }}
            onReject={reject}
          />
        ))
      )}

      {confirmRec && (
        <ConfirmDialog
          rec={confirmRec.rec}
          mode={confirmRec.mode}
          onCancel={() => setConfirmRec(null)}
          onConfirmMedium={confirmMedium}
          onOverrideLow={overrideLow}
          busy={busy}
        />
      )}
    </div>
  );
}

function RecommendationRow({ rec, busy, locked, onAccept, onReject }) {
  const band = rec.confidence_band_at_emit;
  const isOpen = rec.status === "open";
  const bank = rec.bank_transactions?.[0];
  const gl = rec.gl_transactions?.[0];

  return (
    <div data-testid={`rec-row-${rec.id}`} style={{
      padding: "10px 12px", marginBottom: 8,
      border: "1px solid var(--line)", borderRadius: 6,
      background: rec.status !== "open" ? "var(--surface-elev)" : "var(--surface)",
      opacity: rec.status !== "open" ? 0.7 : 1,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span data-testid={`rec-band-${rec.id}`} style={{
            color: BAND_COLOR[band],
            fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em",
          }}>{BAND_LABEL[band] || band}</span>
          <span className="mono" style={{ fontSize: 13, fontWeight: 600 }}>{rec.confidence.toFixed(3)}</span>
          <span className="muted" style={{ fontSize: 11 }}>{rec.rule_version}</span>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {isOpen && !locked && (
            <>
              <button
                className="btn primary btn-sm"
                onClick={() => onAccept(rec)} disabled={busy}
                data-testid={`rec-accept-${rec.id}`}
                style={{ padding: "3px 10px", fontSize: 11 }}
              >Accept</button>
              <button
                className="btn btn-sm"
                onClick={() => onReject(rec)} disabled={busy}
                data-testid={`rec-reject-${rec.id}`}
                style={{ padding: "3px 10px", fontSize: 11 }}
              >Reject</button>
            </>
          )}
          {!isOpen && (
            <span className={"tag " + (rec.status === "accepted" || rec.status === "overridden" ? "ok" : "idle")}
                  data-testid={`rec-status-${rec.id}`}>
              {rec.status}
            </span>
          )}
        </div>
      </div>

      {bank && gl && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 6 }}>
          <PairRow side="Bank" t={bank} />
          <PairRow side="GL" t={gl} />
        </div>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        {(rec.signals || []).map((s, i) => (
          <span key={i} data-testid={`rec-signal-${rec.id}-${s.name}`} style={{
            fontSize: 10, padding: "1px 6px",
            background: "var(--surface-elev)", border: "1px solid var(--line)", borderRadius: 999,
          }}>
            <span style={{ fontWeight: 600 }}>{s.name}</span>
            <span className="mono" style={{ marginLeft: 4 }}>+{s.contributed_score.toFixed(2)}</span>
          </span>
        ))}
      </div>

      {rec.status !== "open" && rec.disposition_notes && (
        <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
          <b>{rec.status === "rejected" ? "Reason:" : "Override:"}</b> {rec.disposition_notes}
        </div>
      )}
    </div>
  );
}

function PairRow({ side, t }) {
  return (
    <div style={{
      padding: "6px 8px", border: "1px solid var(--line)", borderRadius: 4,
      background: "var(--surface-elev)", fontSize: 12,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
        <span style={{ fontWeight: 600 }}>{side}</span>
        <span className="mono">{t.dr_cr} ${fmt.currency(t.amount_unsigned)}</span>
      </div>
      <div className="muted" style={{ fontSize: 11 }}>
        {t.transaction_date} · {t.description?.slice(0, 32) || ""}
      </div>
      {t.reference && (
        <div className="mono muted" style={{ fontSize: 10 }}>ref {t.reference}</div>
      )}
    </div>
  );
}

function ConfirmDialog({ rec, mode, onCancel, onConfirmMedium, onOverrideLow, busy }) {
  const [rationale, setRationale] = useState("");
  return (
    <div onClick={onCancel} style={{
      position: "fixed", inset: 0, background: "rgba(20,20,20,0.32)",
      zIndex: 70, display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div onClick={e => e.stopPropagation()} data-testid="rec-confirm-dialog" style={{
        background: "var(--surface)", width: "min(560px, 92vw)",
        padding: "22px 26px", borderRadius: 8, border: "1px solid var(--line)",
        boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
      }}>
        <div className="heading h2" style={{ marginBottom: 6 }}>
          {mode === "medium" ? "Confirm Acceptance" : "Override Required"}
        </div>
        <div className="muted" style={{ fontSize: 13, marginBottom: 14 }}>
          {mode === "medium"
            ? <>This is a <b>medium-confidence</b> match (score {rec.confidence.toFixed(3)}). Review the signals and confirm to create the MatchGroup.</>
            : <>This is a <b>low-confidence</b> match (score {rec.confidence.toFixed(3)}). The Recognition Engine flagged it as below the standard acceptance threshold. Provide an override rationale to accept it anyway.</>}
        </div>

        <div style={{ marginBottom: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {rec.bank_transactions?.[0] && <PairRow side="Bank" t={rec.bank_transactions[0]} />}
          {rec.gl_transactions?.[0] && <PairRow side="GL" t={rec.gl_transactions[0]} />}
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 14 }}>
          {(rec.signals || []).map((s, i) => (
            <span key={i} style={{
              fontSize: 11, padding: "2px 8px",
              background: "var(--surface-elev)", border: "1px solid var(--line)", borderRadius: 999,
            }}>
              <span style={{ fontWeight: 600 }}>{s.name}</span>
              <span className="mono" style={{ marginLeft: 4 }}>+{s.contributed_score.toFixed(2)}</span>
            </span>
          ))}
        </div>

        {mode === "low" && (
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, marginBottom: 12 }}>
            <span className="muted">Override rationale (required)</span>
            <textarea
              value={rationale}
              onChange={e => setRationale(e.target.value)}
              rows={3}
              data-testid="rec-override-rationale"
              placeholder="Why is this match correct despite the low engine score?"
              style={{
                padding: 6, fontSize: 13, fontFamily: "inherit",
                background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 4,
              }}
            />
          </label>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn btn-sm" onClick={onCancel} disabled={busy}>Cancel</button>
          {mode === "medium" ? (
            <button className="btn primary btn-sm" onClick={onConfirmMedium} disabled={busy}
                    data-testid="rec-confirm-medium-btn">
              {busy ? "Creating MatchGroup…" : "Confirm & Create Match"}
            </button>
          ) : (
            <button className="btn primary btn-sm" onClick={() => onOverrideLow(rationale)} disabled={busy}
                    data-testid="rec-override-low-btn">
              {busy ? "Overriding…" : "Override & Create Match"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, color, testid }) {
  return (
    <div data-testid={testid} style={{
      padding: "6px 8px", border: "1px solid var(--line)", borderRadius: 4,
      background: "var(--surface-elev)", textAlign: "center",
    }}>
      <div className="muted" style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
      <div className="mono" style={{ fontSize: 16, fontWeight: 700, color: color || "inherit" }}>{value}</div>
    </div>
  );
}
